import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Box } from "@mui/material";
import useForm from "../common/AsyncFormBox/useForm";

/**
 * RadioInput - Radio button group component with form integration
 * Renders a FormControl with RadioGroup and supports native browser validation
 *
 * @param {Object} props
 * @param {string} props.name - Form field name (required)
 * @param {Array<{value: string, label: string}>} props.radioList - Array of radio options (required)
 * @param {string} [props.label] - Label text for FormLabel
 * @param {string} [props.defaultValue] - Initial selected value
 * @param {boolean} [props.required] - Whether the field is required
 * @param {boolean} [props.fullWidth] - Whether FormControl should be full width
 * @param {boolean} [props.row] - Whether RadioGroup should display in a row (default: false)
 * @param {string} [props.size] - Size of Radio buttons ("small" | "medium", default: "medium")
 * @param {Function} [props.onChange] - Optional callback when value changes, receives the selected value
 * @param {boolean} [props.disabled] - Whether the field is disabled
 */
function RadioInput({
  name,
  radioList,
  label,
  defaultValue,
  required,
  fullWidth,
  row = false,
  size = "medium",
  onChange,
  disabled = false,
}) {
  const form = useForm();
  const [value, setValue] = useState(defaultValue || "");

  // Initialize value from defaultValue and sync with form context
  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== null && defaultValue !== "") {
      setValue(defaultValue);
      form.setValue(name, defaultValue);
    }
  }, [defaultValue, name, form]);

  // Handle selection change
  function handleChange(event) {
    const newValue = event.target.value;
    setValue(newValue);
    form.setValue(name, newValue);
    onChange && onChange(newValue);
  }

  return (
    <Box sx={{ position: "relative" }}>
      <FormControl fullWidth={fullWidth} required={required} disabled={disabled}>
        {label && <FormLabel component="legend">{label}</FormLabel>}
        <RadioGroup row={row} name={name} value={value} onChange={handleChange}>
          {radioList.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size={size} />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {/* Visually hidden input for native browser validation - positioned at bottom */}
      <input
        type="text"
        name={name}
        value={value}
        required={required}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          opacity: 0,
          width: "1px",
          height: "1px",
          pointerEvents: "none",
          zIndex: -1,
        }}
        tabIndex={-1}
        aria-hidden="true"
        onChange={() => {}} // Controlled by RadioGroup
      />
    </Box>
  );
}

RadioInput.propTypes = {
  name: PropTypes.string.isRequired,
  radioList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  row: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

export default RadioInput;
